import React, { useState, useEffect } from "react";
import "../../styles/components/clients/BookingPatternCarousel.css";
import { generateConferenceSalesSlides } from "../../services/conferenceSalesService";

/**
 * ConferenceSalesCarousel
 * Carousel for displaying conference sales data per client per sponsor per year.
 * Synchronized with SponsorCarousel - shows data for the currently selected sponsor.
 * 
 * @param {Object} props
 * @param {string} props.clientName - Client name for data lookup
 * @param {Object} props.currentSponsor - Currently displayed sponsor from SponsorCarousel
 * @param {string} props.selectedYear - Selected year from top bar filter
 * @param {Object} props.conferenceSalesData - Conference sales data from third sheet
 * @param {string} props.cardColor - Color for styling
 */
const ConferenceSalesCarousel = ({ 
  clientName, 
  currentSponsor, 
  selectedYear, 
  conferenceSalesData,
  cardColor 
}) => {
  const [index, setIndex] = useState(0);

  // Extract the full sponsor name (conference + group name)
  // The sponsor displayName is "{conferenceName} {groupName}" which matches the data key
  const sponsorName = currentSponsor?.displayName || '';
  
  // Generate slides based on current sponsor and selected year
  const slides = generateConferenceSalesSlides(
    clientName,
    sponsorName,
    selectedYear,
    conferenceSalesData
  );

  // Reset to first slide when currentSponsor changes
  useEffect(() => {
    setIndex(0);
  }, [currentSponsor]);

  // Also reset when slides change (year filter changed)
  useEffect(() => {
    setIndex(0);
  }, [slides.length, selectedYear]);

  if (!slides || slides.length === 0) {
    return (
      <div className="sponsor-carousel-empty">
        No sales data available.
      </div>
    );
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const total = slides.length;
  const current = index + 1;
  const slide = slides[index];

  return (
    <div className="sponsor-carousel">
      <button
        className="sponsor-carousel-arrow"
        onClick={handlePrev}
        aria-label="Previous year"
        tabIndex={0}
        type="button"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M20 8L12 16L20 24" stroke="black" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div className="sponsor-carousel-info booking-pattern-carousel-outer">
        <div className="booking-pattern-main-title" style={{ color: cardColor }}>
          Sponsor Booking History <span className="booking-pattern-pagination">({current}/{total})</span>
        </div>
        <div className="booking-pattern-carousel-data">
          <div className="booking-pattern-row">
            <span className="booking-pattern-label">Year:</span>
            <span className="booking-pattern-value">{slide.year}</span>
          </div>
          <div className="booking-pattern-row">
            <span className="booking-pattern-label">Rooms:</span>
            <span className="booking-pattern-value">{slide.rooms}</span>
          </div>
          <div className="booking-pattern-row">
            <span className="booking-pattern-label">Room Nights:</span>
            <span className="booking-pattern-value">{slide.rn}</span>
          </div>
          <div className="booking-pattern-row">
            <span className="booking-pattern-label">Revenue:</span>
            <span className="booking-pattern-value">{slide.revenue}</span>
          </div>
        </div>
      </div>
      <button
        className="sponsor-carousel-arrow"
        onClick={handleNext}
        aria-label="Next year"
        tabIndex={0}
        type="button"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M12 8L20 16L12 24" stroke="black" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default ConferenceSalesCarousel;
